import {
  Box,
  Stack,
  Button,
  CircularProgress,
  Dialog,
  Backdrop,
  Skeleton,
  Typography,
} from "@mui/material";
import { useRef, useEffect, useState } from "react";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { useParams, useLocation } from "react-router-dom";
import strings from "global/constants/StringConstants";
import EmailEditor from "react-email-editor";
import {
  getWebFormDetails,
  updateWebForm,
  fetchUnlayerScaffold,
  createWebForm,
} from "./WebFormsService";
import {
  openSuccessNotification,
  handleErrorMessage,
  openErrorNotification,
} from "helpers/methods";
import { aiBuilderPath, sessionOfDesign } from "./AiBuilder/AiWebFormHelper";
import { listAiSessions } from "./AiBuilder/AiWebFormService";
import { WebFormUnsavedDialog } from "./WebFormHelper";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { LiveRegion } from "components/live-region";

const WebFormBuilder = () => {
  useTitle(title.WEBFORMS);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const emailEditorRef = useRef<any>(null);
  const editorReadyRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initialDesign, setInitialDesign] = useState<any>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);

  // New states for Cross-Tab Sync & Validation
  const [activeTemplateId, setActiveTemplateId] = useState<string>("");
  const [activeSignerId, setActiveSignerId] = useState<number>(1);
  const [showUnmappedModal, setShowUnmappedModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<{
    html: string;
    design: any;
  } | null>(null);
  const pendingSaveDataRef = useRef<{ html: string; design: any } | null>(null);

  useEffect(() => {
    pendingSaveDataRef.current = pendingSaveData;
  }, [pendingSaveData]);

  const traverseFormFields = (
    design: any,
    callback: (content: any, fields: any[]) => void | boolean,
  ) => {
    let result = false;
    if (design?.body?.rows) {
      for (const row of design.body.rows) {
        if (row.columns) {
          for (const col of row.columns) {
            if (col.contents) {
              for (const content of col.contents) {
                if (content.type === "form" && content.values?.fields) {
                  if (callback(content, content.values.fields)) {
                    result = true;
                  }
                }
              }
            }
          }
        }
      }
    }
    return result;
  };

  const removeEmailFieldsFromDesign = (design: any) => {
    if (!design) return design;
    const newDesign = JSON.parse(JSON.stringify(design));
    traverseFormFields(newDesign, (content, fields) => {
      content.values.fields = fields.filter(
        (f: any) => !f.name?.toLowerCase().includes("-email-"),
      );
    });
    return newDesign;
  };

  const allowedLabelsRef = useRef<Set<string>>(new Set());

  const extractLabelsFromDesign = (design: any) => {
    const labels = new Set<string>();
    traverseFormFields(design, (_, fields) => {
      fields.forEach((f: any) => {
        if (f.name) labels.add(f.name);
      });
    });
    return labels;
  };

  const getFieldsFromDesign = (design: any): any[] => {
    const fields: any[] = [];
    traverseFormFields(design, (_, formFields) => {
      fields.push(...formFields);
    });
    return fields;
  };

  const injectFieldsIntoDesign = (design: any, missingFields: any[]) => {
    let injected = false;
    traverseFormFields(design, (content, fields) => {
      if (!injected) {
        content.values.fields = [...fields, ...missingFields];
        injected = true;
      }
    });
    return injected;
  };

  const updateExistingFieldsInDesign = (design: any, newFields: any[]) => {
    return traverseFormFields(design, (content, fields) => {
      let updated = false;
      content.values.fields = fields.map((f: any) => {
        const newField = newFields.find((nf) => nf.name === f.name);
        if (newField) {
          updated = true;
          return { ...f, ...newField };
        }
        return f;
      });
      return updated;
    });
  };

  const removeUnmappedFieldsFromDesign = (design: any, newFields: any[]) => {
    return traverseFormFields(design, (content, fields) => {
      if (newFields.length === 0) return false;
      const originalLength = fields.length;
      content.values.fields = fields.filter((f: any) =>
        newFields.find((nf) => nf.name === f.name),
      );
      return content.values.fields.length !== originalLength;
    });
  };

  useEffect(() => {
    let cancelled = false;

    const loadDesign = async () => {
      try {
        if (id === "new") {
          const searchParams = new URLSearchParams(location.search);
          const templateId = searchParams.get("templateId");
          const signerId = searchParams.get("signerId")
            ? Number(searchParams.get("signerId"))
            : 1;

          if (templateId) {
            setActiveTemplateId(templateId);
            setActiveSignerId(signerId);
            let data = await fetchUnlayerScaffold(templateId, signerId);
            if (!cancelled) {
              data = removeEmailFieldsFromDesign(data);
              setInitialDesign(data);
              allowedLabelsRef.current = extractLabelsFromDesign(data);
            }
          }
        } else if (id) {
          const webForm = await getWebFormDetails(id);
          if (!webForm) return;

          // If this form was created with the AI designer, redirect to the AI builder
          const isAi = Boolean(webForm.aiDesignId || (!webForm.unlayerJson && webForm.htmlKey));
          if (isAi) {
            let sessionId = webForm.aiSessionId;
            if (!sessionId) {
              try {
                const list = await listAiSessions({
                  templateId: webForm.templateId,
                  signerId: webForm.signerId,
                  limit: 50,
                });
                sessionId = webForm.aiDesignId
                  ? sessionOfDesign(list.sessions, webForm.aiDesignId)
                  : list.sessions?.[0]?.sessionId;
              } catch (e) {
                console.error("Failed to lookup AI session", e);
              }
            }
            if (sessionId) {
              history.replace(
                aiBuilderPath({
                  sessionId,
                  templateId: webForm.templateId,
                  signerId: webForm.signerId,
                  name: webForm.name,
                }),
              );
              return;
            }
          }

          const actualTemplateId = webForm.templateId;
          const actualSignerId = webForm.signerId;

          setActiveTemplateId(actualTemplateId);
          setActiveSignerId(actualSignerId);

          let scaffoldData = await fetchUnlayerScaffold(
            actualTemplateId,
            actualSignerId,
          );
          scaffoldData = removeEmailFieldsFromDesign(scaffoldData);
          if (!cancelled) {
            allowedLabelsRef.current = extractLabelsFromDesign(scaffoldData);
          }

          if (webForm.unlayerJson) {
            try {
              if (!cancelled) {
                const parseDesign = JSON.parse(webForm.unlayerJson);
                setInitialDesign(removeEmailFieldsFromDesign(parseDesign));
              }
            } catch (e) {
              console.error("Failed to parse unlayerJson", e);
            }
          } else {
            if (!cancelled) setInitialDesign(scaffoldData);
          }
        }
      } catch (err: any) {
        if (!cancelled) handleErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDesign();

    return () => {
      cancelled = true;
    };
  }, [id, location.search]);

  // Cross-Tab Sync via BroadcastChannel
  useEffect(() => {
    if (!activeTemplateId) return;

    const channel = new BroadcastChannel("template_updates");
    channel.onmessage = async (event) => {
      if (
        event.data.type === "TEMPLATE_UPDATED" &&
        event.data.templateId === activeTemplateId
      ) {
        try {
          setSaving(true);
          let scaffoldData = await fetchUnlayerScaffold(
            activeTemplateId,
            activeSignerId,
          );
          scaffoldData = removeEmailFieldsFromDesign(scaffoldData);
          allowedLabelsRef.current = extractLabelsFromDesign(scaffoldData);

          if (id === "new") {
            setInitialDesign(scaffoldData);
            emailEditorRef.current?.editor?.loadDesign(scaffoldData);
            setShowUnmappedModal(false);
            openSuccessNotification("Template updates synced successfully!");
            setSaving(false);
          } else {
            if (emailEditorRef.current?.editor) {
              emailEditorRef.current.editor.exportHtml((data: any) => {
                if (data && data.design) {
                  const currentDesign = JSON.parse(JSON.stringify(data.design));
                  const newFields = getFieldsFromDesign(scaffoldData);
                  const currentFields = getFieldsFromDesign(currentDesign);

                  const missingFields = newFields.filter(
                    (nf) => !currentFields.find((cf) => cf.name === nf.name),
                  );

                  let designModified = false;

                  if (missingFields.length > 0) {
                    injectFieldsIntoDesign(currentDesign, missingFields);
                    designModified = true;
                  }

                  if (updateExistingFieldsInDesign(currentDesign, newFields)) {
                    designModified = true;
                  }

                  if (
                    removeUnmappedFieldsFromDesign(currentDesign, newFields)
                  ) {
                    designModified = true;
                  }
                  if (designModified) {
                    emailEditorRef.current?.editor?.loadDesign(currentDesign);
                  }

                  setShowUnmappedModal(false);
                  openSuccessNotification(
                    "Template updates synced successfully!",
                  );
                }
                setSaving(false);
              });
            } else {
              setSaving(false);
            }
          }
        } catch (e) {
          console.error(e);
          setSaving(false);
        }
      }
    };

    return () => {
      channel.close();
    };
  }, [activeTemplateId, activeSignerId]);

  const executeSave = (html: string, design: any) => {
    if (id === "new") {
      const searchParams = new URLSearchParams(location.search);
      const templateId = searchParams.get("templateId") || "";
      const signerId = searchParams.get("signerId")
        ? Number(searchParams.get("signerId"))
        : 1;
      const name = searchParams.get("name") || "Untitled Form";

      const formData = new FormData();
      formData.append("templateId", templateId);
      formData.append("signerId", signerId.toString());
      formData.append("name", name);
      if (html) {
        const blob = new Blob([html], { type: "text/html" });
        formData.append("html", blob, `webform.html`);
      }
      if (design) {
        formData.append("unlayerJson", JSON.stringify(design));
      }

      createWebForm(formData)
        .then(() => {
          setSaving(false);
          openSuccessNotification("Web Form created successfully!");
          history.push(viewpaths.webFormsViewPath);
        })
        .catch((err) => {
          setSaving(false);
          handleErrorMessage(err);
        });
    } else {
      const formData = new FormData();
      if (html) {
        const blob = new Blob([html], { type: "text/html" });
        formData.append("html", blob, `webform.html`);
      }
      if (design) {
        formData.append("unlayerJson", JSON.stringify(design));
      }
      updateWebForm(id as string, formData)
        .then(() => {
          setSaving(false);
          openSuccessNotification("Design saved successfully!");
          history.push(viewpaths.webFormsViewPath);
        })
        .catch((err: any) => {
          setSaving(false);
          handleErrorMessage(err);
        });
    }
  };

  const saveDesign = (
    forceSave = false,
    bypassData?: { html: string; design: any },
  ) => {
    if (!id) return;

    if (forceSave && bypassData) {
      setSaving(true);
      setShowUnmappedModal(false);
      executeSave(bypassData.html, bypassData.design);
      return;
    }

    const editorInstance = emailEditorRef.current?.editor;
    if (!editorInstance) {
      setSaving(false);
      return;
    }

    setSaving(true);

    editorInstance.exportHtml((data: any) => {
      if (!data) {
        setSaving(false);
        return;
      }
      const { html, design } = data;

      if (design && design.body && design.body.rows) {
        let hasMissingLabel = false;
        let missingTypes = new Set<string>();
        let hasUnmapped = false;

        design.body.rows.forEach((row: any) => {
          row.columns?.forEach((col: any) => {
            col.contents?.forEach((content: any) => {
              if (content.type === "form" && content.values?.fields) {
                content.values.fields.forEach((f: any) => {
                  if (
                    !f.name ||
                    f.name.trim() === "" ||
                    !f.label ||
                    f.label.trim() === ""
                  ) {
                    hasMissingLabel = true;
                    const formattedType = f.type
                      ? f.type.charAt(0).toUpperCase() +
                        f.type.slice(1).replace(/_/g, " ")
                      : "Unknown";
                    missingTypes.add(formattedType);
                  }

                  if (f.name && !allowedLabelsRef.current.has(f.name)) {
                    hasUnmapped = true;
                  }
                });
              }
            });
          });
        });

        if (hasMissingLabel) {
          const typesString = Array.from(missingTypes).join(", ");
          openErrorNotification(
            `Missing Data Label! Please assign a Data Label to the ${typesString} field. Select the form and update its properties.`,
          );
          setSaving(false);
          return;
        }

        if (hasUnmapped) {
          setPendingSaveData({ html, design });
          setShowUnmappedModal(true);
          setSaving(false);
          return;
        }
      }

      executeSave(html, design);
    });
  };

  useEffect(() => {
    if (initialDesign && editorReadyRef.current) {
      emailEditorRef.current?.editor?.loadDesign(initialDesign);
    }
  }, [initialDesign]);

  const onReady = () => {
    editorReadyRef.current = true;
    setIsEditorReady(true);
    if (initialDesign) {
      emailEditorRef.current?.editor?.loadDesign(initialDesign);
    }
  };

  return (
    <>
      {/*
        Not a Modal System overlay: this is the always-open, full-screen form
        designer — a page surface rather than a dialog. The design system's own
        rule keeps long-form editing out of a modal shell, so it stays a plain
        full-screen `Dialog`. The prompts it raises (below) do go through
        `AppModal`.
      */}
      <Dialog
        fullWidth
        fullScreen
        open={true}
        onClose={() => history.push(viewpaths.webFormsViewPath)}
        PaperProps={{ role: "dialog", "aria-label": "Form editor" }}
      >
        <Backdrop
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
          }}
          open={saving}
        >
          <CircularProgress color="primary" aria-label="Saving form" />
        </Backdrop>
        <LiveRegion
          id="webform-save-status"
          message={saving ? "Saving form" : ""}
        />
        <Stack spacing={1} height="100vh !important">
          <Stack
            p={2}
            direction="row"
            justifyContent="end"
            alignItems="center"
            component="header"
            role="banner"
          >
            <Stack
              direction="row"
              spacing={2}
              role="toolbar"
              aria-label="Form editor actions"
            >
              {loading || !isEditorReady ? (
                <>
                  <Skeleton variant="rounded" width={80} height={36} />
                  <Skeleton variant="rounded" width={110} height={36} />
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => setShowUnsavedDialog(true)}
                    aria-label="Cancel and close designer"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => saveDesign()}
                    disabled={saving || loading}
                    aria-label="Save form design"
                  >
                    {saving ? "Saving..." : "Save Form"}
                  </Button>
                </>
              )}
            </Stack>
          </Stack>

          {/* Unlayer Editor Canvas */}
          <Box
            role="region"
            aria-label="Form design editor canvas"
            tabIndex={0}
            sx={{ flex: 1, position: "relative" }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              ></Box>
            ) : (
              <EmailEditor
                ref={emailEditorRef}
                onReady={onReady}
                minHeight={850}
                projectId={strings.UNLAYER_PROJECT_ID}
                options={
                  {
                    version: "latest",
                    displayMode: "web",
                    appearance: { panels: { tools: { dock: "left" } } },
                    tools: {
                      form: {
                        enabled: true,
                        properties: {
                          fields: {
                            editor: {
                              data: {
                                allowAddNewField: true,
                                defaultFields: [],
                              },
                            },
                          },
                        },
                      },
                    },
                  } as any
                }
              />
            )}
          </Box>
        </Stack>
      </Dialog>

      {/* Unmapped Field Warning Dialog */}
      <AppModal
        open={showUnmappedModal}
        onClose={() => setShowUnmappedModal(false)}
        title="Field Not Linked to Template"
        tone="neutral"
        size="md"
        footer={
          <>
            <Button
              variant="outlined"
              onClick={() => setShowUnmappedModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setShowUnmappedModal(false);
                window.open(
                  `${viewpaths.updateTemplateViewPath}/${activeTemplateId}?step=2&fromWebForm=true&signerId=${activeSignerId}`,
                  "_blank",
                );
              }}
            >
              Edit Template
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                if (pendingSaveData) saveDesign(true, pendingSaveData);
              }}
            >
              Save Anyway
            </Button>
          </>
        }
      >
        <Typography sx={bodyTextSx}>
          You have added a field that is not mapped to the underlying Template.
          You can edit the template to add this field, or you can choose to save
          the form anyway.
        </Typography>
      </AppModal>

      {/* Unsaved Changes Confirmation Dialog */}
      <WebFormUnsavedDialog
        open={showUnsavedDialog}
        onClose={() => setShowUnsavedDialog(false)}
        onLeave={() => history.push(viewpaths.webFormsViewPath)}
      />
    </>
  );
};

export default WebFormBuilder;
